"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SUPPORTED_LANGS, SUPPORTED_LOCALE_CODES, getDefaultLanguageForCountry, loadLanguage } from "@/i18n/dictionaries";

const DEFAULT_LOCALE = "en";
const LOCALE_STORAGE_KEY = "jobolar_locale";
const LOCALE_MANUAL_KEY = "jobolar_locale_manual"; // tracks whether user manually chose a language

const LanguageContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  meta: SUPPORTED_LANGS[0],
});

function getStorageLocale() {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALE_CODES.includes(stored)) return stored;
    return DEFAULT_LOCALE;
  } catch (err) {
    console.warn("Failed to read locale from localStorage", err);
    return DEFAULT_LOCALE;
  }
}

function isManuallySet() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(LOCALE_MANUAL_KEY) === "1";
  } catch {
    return false;
  }
}

export function LanguageProvider({ children }) {
  // Always initialize as "en" for SSR/hydration consistency.
  // The stored locale is applied after mount once the language chunk is loaded.
  const [locale, setLocaleRaw] = useState(DEFAULT_LOCALE);

  // On mount: restore saved locale, or detect browser language on first visit
  useEffect(() => {
    const stored = getStorageLocale();
    if (stored !== DEFAULT_LOCALE) {
      loadLanguage(stored).then(() => setLocaleRaw(stored));
      return;
    }
    // No saved locale — detect from browser language (first visit)
    try {
      const browserLang = (navigator.language || "").toLowerCase().split("-")[0];
      if (browserLang && browserLang !== DEFAULT_LOCALE && SUPPORTED_LOCALE_CODES.includes(browserLang)) {
        loadLanguage(browserLang).then(() => setLocaleRaw(browserLang));
      }
    } catch {}
  }, []);

  const setLocale = useCallback((code) => {
    const normalized = SUPPORTED_LOCALE_CODES.includes(code) ? code : DEFAULT_LOCALE;
    // Load the language chunk, then update locale
    loadLanguage(normalized).then(() => {
      setLocaleRaw(normalized);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
          window.localStorage.setItem(LOCALE_MANUAL_KEY, "1");
        } catch {}
      }
    });
  }, []);

  /** Set locale from country code (only if user hasn't manually chosen) */
  const setLocaleFromCountry = useCallback((countryCode) => {
    if (isManuallySet()) return;
    const lang = getDefaultLanguageForCountry(countryCode);
    loadLanguage(lang).then(() => {
      setLocaleRaw(lang);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(LOCALE_STORAGE_KEY, lang);
          // Don't set manual flag — this is automatic
        } catch {}
      }
    });
  }, []);

  const meta = useMemo(
    () => SUPPORTED_LANGS.find((item) => item.code === locale) ?? SUPPORTED_LANGS[0],
    [locale]
  );

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    document.documentElement.dir = "ltr"; // all 4 supported languages are LTR
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      setLocaleFromCountry,
      meta,
      languages: SUPPORTED_LANGS,
    }),
    [locale, setLocale, setLocaleFromCountry, meta]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageContext;
